import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, IconArrowLeft, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { formatNumber, t, type MessageKey } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { catalogApi, variantName, type Offer, type Product, type TemplateCard } from "./api.ts";
import { CATALOG, partnersQuery, templateQuery } from "./queries.ts";

const route = getRouteApi("/app/catalog/templates/$id");

const KIND: Record<TemplateCard["kind"], MessageKey> = {
  COMPONENT: "catalog.kind.COMPONENT",
  PRODUCT: "catalog.kind.PRODUCT",
  SERVICE: "catalog.kind.SERVICE",
};

const TAG: Record<TemplateCard["tags"][number], MessageKey> = {
  CONSUMABLE: "catalog.tag.CONSUMABLE",
  TRACE_CANDIDATE: "catalog.tag.TRACE_CANDIDATE",
  IMPORT: "catalog.tag.IMPORT",
};

/**
 * Карточка товара: варианты с артикулами (у изделия по значению «Версії»,
 * CAT-11), строки поставщиков с их артикулом и названием (это псевдонимы
 * для поиска, CAT-08), MOQ, кратностью и сроком. Цена 0 значит «не
 * звірено»: заказ возьмёт себестоимость оценкой (ЗК-11).
 */
export function TemplatePage() {
  const { id } = route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const card = useQuery(templateQuery(id));
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn() });

  const act = (fn: () => Promise<unknown>, ok?: NoticeState, open?: (r: unknown) => string) =>
    run.mutate(fn, {
      onSuccess: (r) => {
        setNotice(ok ?? null);
        void queryClient.invalidateQueries({ queryKey: CATALOG });
        // «Дублювати» открывает копию; остальные действия остаются в карточке.
        if (open) void navigate({ to: "/catalog/templates/$id", params: { id: open(r) } });
      },
      onError: (e) => setNotice(errorNotice(e)),
    });

  const c = card.data;
  if (card.error) return <Notice tone="danger" title={errorNotice(card.error).title} className="page" />;
  if (!c) return null;

  return (
    <div className="page catalog">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/catalog/products" aria-label={t("catalog.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title">{c.name}</h1>
        {c.archived ? <Badge>{t("catalog.archived")}</Badge> : null}
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}

      <Plate cut="lg" className="order-head">
        <dl className="order-meta">
          <div className="order-meta-item">
            <dt>{t("catalog.category")}</dt>
            <dd>{c.products[0]?.categoryPath ?? "—"}</dd>
          </div>
          <div className="order-meta-item">
            <dt>{t("catalog.kind")}</dt>
            <dd>{t(KIND[c.kind])}</dd>
          </div>
          {c.tags.length > 0 ? (
            <div className="order-meta-item">
              <dt>{t("catalog.tags")}</dt>
              <dd className="tags">
                {c.tags.map((tag) => (
                  <Badge key={tag}>{t(TAG[tag])}</Badge>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
        <RenameForm card={c} busy={run.isPending} onSave={(name) => act(() => catalogApi.editTemplate(c.id, { name }), { tone: "success", title: t("catalog.saved") })} />
      </Plate>

      <section className="stock-section" aria-labelledby="cat-variants">
        <h2 id="cat-variants" className="desk-group-title">
          {t("catalog.variants")}
        </h2>
        <ul className="hint-list">
          {c.products.map((p) => (
            <VariantRow key={p.id} product={p} busy={run.isPending} onCode={(code) => act(() => catalogApi.setCode(p.id, code), { tone: "success", title: t("catalog.codeSaved", { code }) })} />
          ))}
        </ul>
        {c.kind === "PRODUCT" ? (
          <VersionForm
            card={c}
            busy={run.isPending}
            onAdd={(name) => act(() => catalogApi.addVersion(c.id, name, null), { tone: "success", title: t("catalog.versionAdded", { version: name }) })}
            onArchive={(v) => act(() => catalogApi.archiveVersion(c.id, v))}
          />
        ) : null}
      </section>

      <section className="stock-section" aria-labelledby="cat-offers">
        <h2 id="cat-offers" className="desk-group-title">
          {t("catalog.offers")}
        </h2>
        {c.offers.length === 0 ? <p className="w-muted">{t("catalog.noOffers")}</p> : null}
        <ul className="hint-list">
          {c.offers.map((o) => (
            <OfferRow key={o.id} offer={o} />
          ))}
        </ul>
        {c.products[0] ? <OfferForm productId={c.products[0].id} busy={run.isPending} act={act} /> : null}
      </section>

      <div className="order-actions">
        <Button disabled={run.isPending} onClick={() => act(() => catalogApi.duplicate(c.id), { tone: "success", title: t("catalog.duplicated") }, (r) => (r as TemplateCard).id)}>
          {t("catalog.duplicate")}
        </Button>
        {c.archived ? (
          <Button disabled={run.isPending} onClick={() => act(() => catalogApi.restore(c.id), { tone: "success", title: t("catalog.restored") })}>
            {t("catalog.restore")}
          </Button>
        ) : (
          <Button variant="ghost" disabled={run.isPending} onClick={() => act(() => catalogApi.archive(c.id), { tone: "info", title: t("catalog.archivedDone") })}>
            {t("catalog.archive")}
          </Button>
        )}
      </div>
    </div>
  );
}

function RenameForm({ card, busy, onSave }: { card: TemplateCard; busy: boolean; onSave: (name: string) => void }) {
  const [name, setName] = useState(card.name);
  return (
    <form
      className="qty-row catalog-rename"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && name.trim() !== card.name) onSave(name.trim());
      }}
    >
      <TextField label={t("catalog.name")} value={name} onChange={(e) => setName(e.target.value)} className="qty-field" />
      <Button type="submit" disabled={busy || !name.trim() || name.trim() === card.name} className="qty-submit">
        {t("order.save")}
      </Button>
    </form>
  );
}

/** Вариант: артикул правится руками, уникален с учётом архива (CAT-04, CAT-05). */
function VariantRow({ product: p, busy, onCode }: { product: Product; busy: boolean; onCode: (code: string) => void }) {
  const [code, setCode] = useState(p.code ?? "");
  return (
    <li className="hint variant">
      <span className="hint-name">
        {variantName(p)}
        {p.archived ? <Badge>{t("catalog.archived")}</Badge> : null}
      </span>
      <form
        className="variant-code"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim() && code.trim() !== p.code) onCode(code.trim());
        }}
      >
        <TextField label={t("catalog.codeFor", { name: variantName(p) })} hideLabel value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" className="variant-input" />
        <Button type="submit" disabled={busy || !code.trim() || code.trim() === p.code}>
          {t("order.save")}
        </Button>
      </form>
    </li>
  );
}

function VersionForm({ card, busy, onAdd, onArchive }: { card: TemplateCard; busy: boolean; onAdd: (name: string) => void; onArchive: (v: string) => void }) {
  const [name, setName] = useState("");
  return (
    <div className="versions">
      <p className="w-muted">{t("catalog.versionsLead")}</p>
      <ul className="tags">
        {card.versions.map((v) => (
          <li key={v.name} className="version">
            <span className="w-num">{v.name}</span>
            {v.archived ? (
              <Badge>{t("catalog.archived")}</Badge>
            ) : (
              <button type="button" className="advice-use" disabled={busy} onClick={() => onArchive(v.name)}>
                {t("catalog.archive")}
              </button>
            )}
          </li>
        ))}
      </ul>
      <form
        className="qty-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) {
            onAdd(name.trim());
            setName("");
          }
        }}
      >
        <TextField label={t("catalog.newVersion")} value={name} onChange={(e) => setName(e.target.value)} className="qty-field" autoComplete="off" />
        <Button type="submit" disabled={busy || !name.trim()} className="qty-submit">
          {t("catalog.addVersion")}
        </Button>
      </form>
    </div>
  );
}

function OfferRow({ offer: o }: { offer: Offer }) {
  const facts = [
    o.moq !== null && o.moq !== undefined ? t("catalog.moq", { qty: formatNumber(o.moq) }) : null,
    o.packMultiple !== null && o.packMultiple !== undefined ? t("catalog.pack", { qty: formatNumber(o.packMultiple) }) : null,
    o.leadDays !== null && o.leadDays !== undefined ? t("catalog.lead", { days: o.leadDays }) : null,
  ].filter((x): x is string => x !== null);
  // Без срока, MOQ или кратности подсказки дозаказа слепы (ЗК-07): подсвечиваем.
  const missing = o.moq == null || o.packMultiple == null || o.leadDays == null;
  return (
    <li className="hint offer">
      <span className="hint-name">{o.supplierPartner ?? o.supplierId}</span>
      <span className="hint-why w-muted">
        {[o.supplierCode, o.supplierName].filter(Boolean).join(" · ") || t("catalog.sameName")}
        {facts.length ? ` · ${facts.join(" · ")}` : ""}
        {missing ? <span className="advice-warn">{` · ${t("purchasing.advice.missing")}`}</span> : null}
      </span>
      <span className="offer-price">
        {o.verified && o.price > 0 ? <span className="w-num">{formatNumber(o.price)}</span> : <Badge tone="warning">{t("catalog.notVerified")}</Badge>}
      </span>
    </li>
  );
}

function OfferForm({ productId, busy, act }: { productId: string; busy: boolean; act: (fn: () => Promise<unknown>, ok?: NoticeState) => void }) {
  const suppliers = useQuery(partnersQuery(""));
  const [supplierId, setSupplierId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("0");
  const [moq, setMoq] = useState("");
  const [pack, setPack] = useState("");
  const [lead, setLead] = useState("");
  const num = (s: string) => (s.trim() ? parseQty(s) : null);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    act(
      () =>
        catalogApi.addOffer(productId, {
          supplierId,
          supplierCode: code.trim() || null,
          supplierName: name.trim() || null,
          price: num(price) ?? 0,
          moq: num(moq),
          packMultiple: num(pack),
          leadDays: num(lead),
        }),
      { tone: "success", title: t("catalog.offerAdded") },
    );
  };

  return (
    <form className="offer-form" onSubmit={submit} aria-label={t("catalog.addOffer")}>
      <SelectField
        label={t("purchasing.supplier")}
        value={supplierId}
        onChange={(e) => setSupplierId(e.target.value)}
        options={[{ value: "", label: t("purchasing.chooseSupplier") }, ...(suppliers.data ?? []).filter((p) => p.supplier && !p.archived).map((p) => ({ value: p.id, label: p.name }))]}
      />
      <TextField label={t("catalog.supplierCode")} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
      <TextField label={t("catalog.supplierName")} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      <TextField label={t("catalog.price")} hint={t("catalog.priceHint")} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
      <TextField label={t("catalog.moqLabel")} inputMode="decimal" value={moq} onChange={(e) => setMoq(e.target.value)} />
      <TextField label={t("catalog.packLabel")} inputMode="decimal" value={pack} onChange={(e) => setPack(e.target.value)} />
      <TextField label={t("catalog.leadLabel")} inputMode="numeric" value={lead} onChange={(e) => setLead(e.target.value)} />
      <Button type="submit" disabled={busy || !supplierId}>
        {t("catalog.addOffer")}
      </Button>
    </form>
  );
}
